// v193 / v223 — Artifact detail page.
//
// First page that talks per-artifact. Today the surface is two
// stacked cards: Provenance (item 33 Phase B / item 73 Phase B —
// which CI build produced this artifact, with Source / Build /
// Signing breakdown) and Dependencies (item 17 — manifest-extracted
// dependency tree). Future tabs (downloads, related findings) land
// on the same path.
//
// Provenance card lives above the dependency tree because it
// answers the more common operator question first: "where did this
// JAR come from?". It only renders when the artifact-builds
// endpoint returns at least one build; for artifacts uploaded
// outside CI it stays hidden so the page doesn't read as broken.
//
// Tree rendering is intentionally low-fi: nested <ul> with collapse-
// on-click. The roadmap mentions Mermaid / D3 as a stretch goal but
// nesting + indentation gives the operator the same information
// density without dragging in a new render dependency on the daily-
// driver bundle.

import { useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  GitBranch,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { BuildInfoCard } from '../components/BuildInfoCard';
import { useArtifactDeps, type DepNode } from '../api/artifactDeps';
import { useArtifactBuilds } from '../api/buildInfo';
import { deleteArtifact } from '../api/client';

const KIND_TONE: Record<string, 'accent' | 'nebula' | 'neutral' | 'warning'> = {
  compile: 'accent',
  runtime: 'accent',
  provided: 'nebula',
  test: 'neutral',
  dev: 'neutral',
  optional: 'neutral',
  peer: 'nebula',
};

function depthFromQuery(sp: URLSearchParams): number {
  const raw = sp.get('depth');
  if (!raw) return 3;
  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n) || n < 0) return 3;
  if (n > 8) return 8;
  return n;
}

interface NodeRowProps {
  readonly node: DepNode;
  readonly level: number;
  readonly isRoot?: boolean;
}

function NodeRow({ node, level, isRoot }: NodeRowProps) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const [open, setOpen] = useState<boolean>(level < 1);
  const tone = node.kind ? KIND_TONE[node.kind] ?? 'neutral' : 'neutral';

  return (
    <li>
      <div
        className="flex items-start gap-2 py-1 group"
        style={{ paddingLeft: `${level * 18}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-0.5 text-fg-muted hover:text-fg transition-colors"
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="mt-0.5 inline-block h-3.5 w-3.5" />
        )}

        <span
          className={`orb-code text-xs whitespace-nowrap ${
            node.in_registry ? 'text-fg' : 'text-fg-muted'
          }`}
          title={node.in_registry ? 'Resolved in this registry' : 'Not in this registry'}
        >
          {node.coord || '(unknown)'}
        </span>

        {node.kind && !isRoot && (
          <Badge tone={tone}>{node.kind}</Badge>
        )}

        {node.truncated && (
          <Badge tone="warning" title="Cycle — children omitted">
            cycle
          </Badge>
        )}

        {!node.in_registry && !isRoot && (
          <span className="text-[10px] text-fg-subtle uppercase tracking-wide">
            external
          </span>
        )}

        {node.in_registry && node.id && !isRoot && (
          <Link
            to={`/artifacts/${node.id}`}
            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-fg-subtle hover:text-accent"
            title="Open this artifact"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>

      {hasChildren && open && (
        <ul className="border-l border-border/40 ml-2">
          {node.children!.map((child, idx) => (
            <NodeRow
              key={`${child.coord}-${child.kind ?? ''}-${idx}`}
              node={child}
              level={level + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function ArtifactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const depth = depthFromQuery(sp);

  // Move this artifact version to trash (soft-delete). Recoverable from /trash
  // for 30 days; backend enforces project_admin.
  const onTrash = async () => {
    if (!id) return;
    if (!confirm('Move this artifact version to the trash? It stays recoverable in Trash for 30 days, then the reaper removes it.')) return;
    try {
      await deleteArtifact(id);
      navigate('/repos');
    } catch (e) {
      alert(`Could not move to trash: ${e instanceof Error ? e.message : e}`);
    }
  };

  const { data, error, isLoading } = useArtifactDeps(id, depth);
  const { data: provenance } = useArtifactBuilds(id);
  const provenanceBuilds = provenance?.builds ?? [];

  function setDepth(next: number) {
    const np = new URLSearchParams(sp);
    np.set('depth', String(next));
    setSp(np, { replace: true });
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3 text-sm">
        <Link
          to="/repos"
          className="inline-flex items-center gap-1 text-fg-muted hover:text-fg transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to repos
        </Link>
      </div>

      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-fg flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-accent" />
          Artifact detail
        </h1>
        <div className="flex items-center gap-2">
          <span className="orb-code text-[11px] text-fg-muted" title={id}>
            {id?.slice(0, 12)}…
          </span>
          <Button
            variant="ghost"
            type="button"
            onClick={onTrash}
            className="text-fg-muted hover:bg-danger/10 hover:text-danger"
            title="Move this version to the trash (recoverable for 30 days)"
          >
            <Trash2 className="h-4 w-4" />
            Trash
          </Button>
        </div>
      </div>

      {provenanceBuilds.length > 0 && <BuildInfoCard builds={provenanceBuilds} />}

      <Card>
        <CardHeader
          title="Dependencies"
          description="Manifest-extracted dependency tree. Resolved children link to their own page; unresolved coords render as external grey nodes."
        />
        <CardBody>
          <div className="flex items-center gap-3 mb-4" role="group" aria-labelledby="artifact-depth-label">
            <label id="artifact-depth-label" className="text-xs text-fg-muted">Depth</label>
            {[1, 2, 3, 4, 5, 6, 8].map((n) => (
              <Button
                key={n}
                size="sm"
                variant={depth === n ? 'primary' : 'ghost'}
                onClick={() => setDepth(n)}
              >
                {n}
              </Button>
            ))}
          </div>

          {isLoading && (
            <p className="text-sm text-fg-muted">Loading dependency tree…</p>
          )}
          {error && (
            <p className="text-sm text-danger">
              Failed to load: {String((error as Error).message ?? error)}
            </p>
          )}
          {data && (
            <ul className="text-sm">
              <NodeRow node={data} level={0} isRoot />
            </ul>
          )}
          {data && (data.children?.length ?? 0) === 0 && (
            <p className="text-xs text-fg-muted mt-4">
              No dependency edges recorded. Maven .pom and npm tarballs
              populate this view at upload time; for other formats the
              tree stays empty until a format-specific extractor lands.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default ArtifactDetail;
